import { Observable, of, throwError } from 'rxjs';

import { UrlQueryResult } from '../types';

import { urlEntityToComponentName } from './urlEntityToComponentName';
import { NOTFOUND_CMP, ResolvedUrl } from './resolve';
import { getKnownRoute, RouteData } from './getKnownRoute';
import { createDebug } from './runtimeDebug';

const debug = createDebug('fetchFromKnownOrNetwork');
export type ResultShape = { urlResolver: UrlQueryResult };

export interface FetchedData {
    data: { urlResolver: UrlQueryResult };
    urlKey: string;
}

export function fetchFromKnownOrNetwork(
    urlKey: string,
    knownRoutes: RouteData[],
    resolveFromNetwork: (urlKey: string) => Observable<FetchedData>,
): Observable<FetchedData> {
    /**
     * First try to lookup a known route to avoid hitting
     * the network at all. This is useful for routes that are not
     * covered by the urlResolver backend, but are known to the frontend
     * such as /customer/*
     */
    const known = getKnownRoute(urlKey, knownRoutes);
    if (known) {
        debug('returned known route', known, 'for', urlKey);
        return of({ data: { urlResolver: known }, urlKey });
    }

    /**
     * Otherwise we try to resolve this pathname with the urlResolver
     * query
     */
    return resolveFromNetwork(urlKey);
}

/**
 * Once gql  has returned some data, try to extract
 * @param data
 * @param urlKey
 */
export function convertDataToResolved({ data, urlKey }: FetchedData): Observable<ResolvedUrl> {
    if (!data) {
        debug('data missing');
        return throwError(new Error(`data missing from urlResolver`));
    }
    if (typeof data.urlResolver === 'undefined') {
        debug("typeof data.urlResolver === 'undefined'");
        return throwError(new Error(`typeof data.urlResolver === 'undefined'`));
    }
    if (data.urlResolver && data.urlResolver.type) {
        debug('data.urlResolver.types exists', data.urlResolver.type);
        const componentName = urlEntityToComponentName(data.urlResolver.type);
        /**
         * Check for redirects (custom module)
         */
        if (typeof data.urlResolver.redirect_type === 'number') {
            if (typeof data.urlResolver.redirect_url === 'string') {
                return of({
                    urlKey,
                    componentName,
                    id: data.urlResolver.id,
                    redirectStatusCode: data.urlResolver.redirect_type,
                    redirectUrl: data.urlResolver.redirect_url,
                });
            }
        }
        /**
         * Check for redirects (custom module)
         */
        const { redirectCode, relative_url } = data.urlResolver as any;
        if (typeof redirectCode === 'number' && redirectCode !== 0) {
            if (typeof relative_url === 'string') {
                return of({
                    urlKey,
                    componentName,
                    id: data.urlResolver.id,
                    redirectStatusCode: redirectCode,
                    /**
                     * Append the '/' here so that redirectUrl
                     * looks like a regular 'url' seen in node
                     */
                    redirectUrl: '/' + relative_url,
                });
            }
        }
        return of({ urlKey, componentName, id: data.urlResolver.id });
    }
    /**
     * Handles null or missing case
     */
    debug('urlResolver.type was not undefined or a string');
    return of({ urlKey, componentName: NOTFOUND_CMP, id: null });
}
